import { TFunction } from 'i18next';
import { BadgeType } from 'src/components/ui/Badge/Badge';
import { localeNumber, numberLocale } from 'src/localization/locale';
import { InvoiceStatus } from 'src/services/invoices';

export const STATUS_BADGE: Record<InvoiceStatus, BadgeType> = {
  open: BadgeType.Neutral,
  partial: BadgeType.Neutral4,
  overdue: BadgeType.Error,
  in_payment: BadgeType.Warning,
  paid: BadgeType.Successful,
  reversed: BadgeType.Neutral3,
  unknown: BadgeType.Neutral5,
};

const CURRENCY_SYMBOLS: Record<string, string> = { EUR: '€', USD: '$', GBP: '£', CHF: 'CHF' };

/** Amount with the separators of the UI language, currency symbol after the number as elsewhere in the portal. */
export const formatMoney = (value: number, currency: string | null | undefined): string => {
  const number = localeNumber(value, { minimumFractionDigits: 2, maximumFractionDigits: 2 });
  if (!currency) return number;
  return `${number}\u00a0${CURRENCY_SYMBOLS[currency] ?? currency}`;
};

export const formatQuantity = (value: number): string => localeNumber(value, { maximumFractionDigits: 3 });

export const formatPercent = (value: number): string => `${localeNumber(value, { maximumFractionDigits: 2 })}\u00a0%`;

export const formatCount = (value: number): string => localeNumber(value, { maximumFractionDigits: 0 });

const UNIT_NAMES = new Set(['units', 'unit', 'einheit', 'einheiten', 'stk', 'stück', 'pcs', 'piece', 'pieces']);

/** Unit names arrive in the accounting user's language; the common piece unit is shown translated. */
export const formatUom = (name: string | null | undefined, t: TFunction): string => {
  if (!name) return '';
  return UNIT_NAMES.has(name.trim().toLowerCase()) ? t('invoices.uom.units') : name;
};

const BILLING_PERIOD = /^billing period:?\s*(\d{4})\s*[/-]\s*(\d{1,2})$/i;

/** "Billing Period: 2026/8" from the accounting system becomes "Abrechnungszeitraum August 2026". */
export const formatReference = (ref: string | null | undefined, t: TFunction): string => {
  if (!ref) return '';
  const match = BILLING_PERIOD.exec(ref.trim());
  if (!match) return ref;
  const period = new Intl.DateTimeFormat(numberLocale(), { month: 'long', year: 'numeric' }).format(
    new Date(Number(match[1]), Number(match[2]) - 1, 1),
  );
  return t('invoices.detail.billing_period', { period });
};
